import {
	DimensionRecommendationKind,
	FormItem,
	RadioButton,
	TextInput,
	ValidationError
} from "client/Components";
import { IObjectInputAttachable, IValueInputAttachable } from "client/Forms/attachable";
import { FormModel } from "./EditCommunityDialog";
import {
	Fragment,
	ReactElement,
	useMemo,
	useState
} from "react";
import { localize } from "client_legacy/ndaw/culture";
import { Guid, newGuid } from "client_legacy/ndaw/guid";
import { ImageSelect } from "../../ImageSelect/ImageSelect";
import {
	CommunityCoverImageSaveModel,
	CommunityCoverImageViewModel,
	CommunityImageRestrictions,
	CommunityVisibility
} from "client/Api/Communities/community";
import { getCommunityImageUrl } from "../common";
import { Base64FileUriUploadWithMetadataModel } from "client/Misc/commonTypes";
import styles from "./Settings.module.less";
import { generateTrackingId } from "client/Misc/utils";

export type SettingsProps = {
	attachable: IValueInputAttachable<FormModel>;
	canOnlyEditCoverImage: boolean;
	originalImage: CommunityCoverImageViewModel | null;
	imageRestrictions: CommunityImageRestrictions;
	onFileChange: (file: Base64FileUriUploadWithMetadataModel | null) => void;
	onEditFocalPoint: () => void;
};

export const Settings = (props: SettingsProps): ReactElement => {
	const {
		attachable,
		canOnlyEditCoverImage,
		originalImage,
		imageRestrictions,
		onFileChange,
		onEditFocalPoint
	} = props;

	const {
		maxFileSize,
		supportedImageTypes
	} = imageRestrictions;

	const imageAttachable: IObjectInputAttachable<CommunityCoverImageSaveModel | null> = attachable.for("image");
	const image: CommunityCoverImageSaveModel | null = imageAttachable.value;

	const {
		value: visibility,
		onChange: onVisibilityChange
	} = attachable.for("visibility")
		.attachToRadioButtonGroup();

	const [visibilityGroupName] = useState<Guid>(() => newGuid());

	const imageSrc = useMemo(
		(): string | null => {
			if (image === null) {
				return null;
			}

			const { file } = image;

			if (file.isSet) {
				return file.value
					.uri;
			}

			if (originalImage) {
				return getCommunityImageUrl(originalImage.file.blobId);
			}

			return null;
		},
		[image, originalImage]
	);

	const isFileValid: boolean | undefined = imageAttachable
		.whenSet(
			(x) => {
				return x.for("file")
					.validation
					.isValid();
			},
			() => undefined
		);

	return (
		<div className={styles.root}>
			<FormItem
				label={localize("_Name")}
				isRequired
			>
				<TextInput
					{...attachable.for("name")
						.attachToTextInput()
					}
					isDisabled={canOnlyEditCoverImage}
				/>

				<ValidationError
					{...attachable.for("name")
						.attachToValidationError()
					}
				/>
			</FormItem>

			<FormItem
				label={localize("_Visibility")}
				isForGroup
			>
				{visibilityOptions.map((x) => (
					<Fragment key={x.value}>
						<RadioButton
							group={visibilityGroupName}
							text={localize(x.textKey)}
							value={x.value === visibility}
							onChange={() => onVisibilityChange(x.value)}
							isDisabled={canOnlyEditCoverImage}
							trackingId={generateTrackingId("visibility", `|${x.trackingId}`)}
						/>

						<FormItem.Description>
							{localize(x.descriptionKey)}
						</FormItem.Description>
					</Fragment>
				))}

				<ValidationError
					{...attachable.for("visibility")
						.attachToValidationError()
					}
				/>
			</FormItem>

			<FormItem
				label={localize("_Description")}
			>
				<TextInput
					{...attachable.for("description")
						.attachToTextInput()
					}
					isDisabled={canOnlyEditCoverImage}
				/>

				<ValidationError
					{...attachable.for("description")
						.attachToValidationError()
					}
				/>
			</FormItem>

			<FormItem label={localize("_Image")}>
				<ImageSelect
					trackingId="select-image"
					src={imageSrc}
					onChange={onFileChange}
					onEditFocalPoint={
						isFileValid === true
							? onEditFocalPoint
							: undefined
					}
					maxImageSize={maxFileSize}
					supportedFormats={supportedImageTypes}
					hasError={!imageAttachable.validation.isValid()}
					dimensionRecommendation={{
						kind: DimensionRecommendationKind.DimensionWithAspectRatio,
						height: 300,
						width: 600,
						aspectRatio: "2:1"
					}}
					supportedFormatsText={[
						localize("_FileExtensionJpg"),
						localize("_FileExtensionPng"),
						localize("_FileExtensionGif")
					]}
				/>

				<ValidationError {...attachable.for("image").attachToValidationError(true)} />

				{attachable.for("image")
					.whenSet(
						(x) => (
							<>
								<ValidationError {...x.for("file").attachToValidationError()} />
								<ValidationError {...x.for("focalPoint").attachToValidationError()} />
							</>
						)
					)
				}
			</FormItem>
		</div>
	);
};
Settings.displayName = "Settings";

type VisibilityOption = {
	value: CommunityVisibility;
	textKey: LocalizationKey;
	descriptionKey: LocalizationKey;
	trackingId: string;
};

const visibilityOptions: readonly VisibilityOption[] = [
	{
		value: CommunityVisibility.Public,
		textKey: "_Public",
		descriptionKey: "_PublicCommunityVisibilityInfo",
		trackingId: "public"
	},
	{
		value: CommunityVisibility.Private,
		textKey: "_Private",
		descriptionKey: "_PrivateCommunityVisibilityInfo",
		trackingId: "private"
	},
	{
		value: CommunityVisibility.Hidden,
		textKey: "_Hidden",
		descriptionKey: "_HiddenCommunityVisibilityInfo",
		trackingId: "hidden"
	}
];
